/**
 * Project Bobcat — the models a solution's AI steps can call through the AI
 * Gateway, by the exact id the step's `model` takes (CallAIModel passes it
 * straight to the gateway's /v1/chat/completions; CallAIClassifier to its
 * /v1/decisions).
 *
 * From Engineering's "Supported AI Models" Confluence page (Tech space, as of
 * 2026-10-06) and the 2026-10-13 gateway release that added OpenAI Decisions.
 * Those were announced for US production; another environment's gateway may
 * not have every one. The gateway sends any id it doesn't list to Gemini.
 */

/** How a model takes `parameters.reasoning_effort`. */
export type ReasoningSupport =
  /** low, medium, high and none. */
  | 'any'
  /** low, medium, high: "none" is an error. */
  | 'no-none'
  /** None at all: sending the parameter is an error. */
  | 'unsupported';

export interface GatewayModel {
  id: string;
  label: string;
  provider: string;
  /** 1 slowest – 5 fastest; 1 lowest – 5 highest. Engineering's estimates. */
  speed: number;
  quality: number;
  reasoning: ReasoningSupport;
  /** Text only: can't read an image or a PDF. */
  textOnly?: boolean;
}

/** What `"model": "default"` (or no model) gets. */
export const GATEWAY_DEFAULT_MODEL = 'gemini-3-flash-preview';

export const GATEWAY_MODELS: readonly GatewayModel[] = [
  {
    id: 'gemini-3-flash-preview',
    label: 'Gemini 3 Flash',
    provider: 'Google',
    speed: 1,
    quality: 5,
    reasoning: 'any',
  },
  {
    id: 'gemini-3.5-flash',
    label: 'Gemini 3.5 Flash',
    provider: 'Google',
    speed: 2,
    quality: 5,
    reasoning: 'any',
  },
  {
    id: 'gemini-3.5-flash-lite',
    label: 'Gemini 3.5 Flash-Lite',
    provider: 'Google',
    speed: 4,
    quality: 3,
    reasoning: 'any',
  },
  {
    id: 'gemini-3.1-flash-lite',
    label: 'Gemini 3.1 Flash-Lite',
    provider: 'Google',
    speed: 4,
    quality: 2,
    reasoning: 'any',
  },
  {
    id: 'openai.gpt-oss-120b-1:0',
    label: 'gpt-oss 120B',
    provider: 'OpenAI',
    speed: 4,
    quality: 4,
    reasoning: 'no-none',
    textOnly: true,
  },
  {
    id: 'openai.gpt-oss-20b-1:0',
    label: 'gpt-oss 20B',
    provider: 'OpenAI',
    speed: 5,
    quality: 3,
    reasoning: 'no-none',
    textOnly: true,
  },
  {
    id: 'google.gemma-3-27b-it',
    label: 'Gemma 3 27B',
    provider: 'Google',
    speed: 2,
    quality: 4,
    reasoning: 'any',
  },
  {
    id: 'google.gemma-3-12b-it',
    label: 'Gemma 3 12B',
    provider: 'Google',
    speed: 3,
    quality: 3,
    reasoning: 'any',
  },
  {
    id: 'google.gemma-3-4b-it',
    label: 'Gemma 3 4B',
    provider: 'Google',
    speed: 5,
    quality: 2,
    reasoning: 'any',
  },
  {
    id: 'mistral.ministral-3-14b-instruct',
    label: 'Ministral 3 14B',
    provider: 'Mistral',
    speed: 3,
    quality: 3,
    reasoning: 'any',
  },
  {
    id: 'mistral.ministral-3-8b-instruct',
    label: 'Ministral 3 8B',
    provider: 'Mistral',
    speed: 4,
    quality: 2,
    reasoning: 'any',
  },
  {
    id: 'mistral.ministral-3-3b-instruct',
    label: 'Ministral 3 3B',
    provider: 'Mistral',
    speed: 5,
    quality: 2,
    reasoning: 'any',
  },
  {
    id: 'us.amazon.nova-2-lite-v1:0',
    label: 'Nova 2 Lite',
    provider: 'Amazon',
    speed: 3,
    quality: 4,
    reasoning: 'unsupported',
  },
  {
    id: 'us.amazon.nova-lite-v1:0',
    label: 'Nova Lite',
    provider: 'Amazon',
    speed: 4,
    quality: 3,
    reasoning: 'unsupported',
  },
  {
    id: 'us.amazon.nova-micro-v1:0',
    label: 'Nova Micro',
    provider: 'Amazon',
    speed: 5,
    quality: 2,
    reasoning: 'unsupported',
    textOnly: true,
  },
  {
    id: 'us.meta.llama4-scout-17b-instruct-v1:0',
    label: 'Llama 4 Scout',
    provider: 'Meta',
    speed: 3,
    quality: 3,
    reasoning: 'unsupported',
  },
];

/** The classifier model CallAIClassifier calls through /v1/decisions. */
export const DECISIONS_MODEL: GatewayModel = {
  id: 'gpt-6-luna',
  label: 'OpenAI Decisions · gpt-6-luna',
  provider: 'OpenAI',
  speed: 5,
  quality: 4,
  reasoning: 'unsupported',
};

/** The commands that call a model through the gateway. */
export const CLASSIFIER_COMMAND = 'CallAIClassifier';
export const GATEWAY_AI_COMMANDS = ['CallAIModel', CLASSIFIER_COMMAND] as const;

export const isGatewayAiCommand = (command: unknown): boolean =>
  (GATEWAY_AI_COMMANDS as readonly string[]).includes(String(command ?? ''));

type Param = Record<string, unknown>;

const parse = (raw: unknown): Param | null => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw))
    return raw as Param;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const value = JSON.parse(raw);
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Param)
      : null;
  } catch {
    return null;
  }
};

/**
 * The model a step's Parameter Input asks for: its `model`, or "default". A
 * `{VARIABLE}` stays as written.
 */
export const readStepModel = (raw: unknown): string => {
  const model = parse(raw)?.model;
  return typeof model === 'string' && model.trim() ? model.trim() : 'default';
};

/** The catalog entry a step's model resolves to; undefined when unknown. */
export const gatewayModel = (
  id: string,
  command?: unknown
): GatewayModel | undefined => {
  if (String(command ?? '') === CLASSIFIER_COMMAND) {
    return id === 'default' || id === DECISIONS_MODEL.id
      ? DECISIONS_MODEL
      : undefined;
  }
  const resolved = id === 'default' ? GATEWAY_DEFAULT_MODEL : id;
  return GATEWAY_MODELS.find((m) => m.id === resolved);
};

/** A step's model in a few words: "Gemini 3 Flash", or the raw id. */
export const stepModelLabel = (raw: unknown, command?: unknown): string => {
  const id = readStepModel(raw);
  return gatewayModel(id, command)?.label ?? id;
};

/**
 * The Parameter Input asking for `modelId`, as the JSON text the table saves;
 * null when it isn't a JSON object. `parameters.reasoning_effort` is fitted
 * to the model, since the gateway rejects a value the model can't take: gone
 * for one that takes none, "none" raised to "low" for one that can't skip it.
 */
export const withStepModel = (raw: unknown, modelId: string): string | null => {
  const data = parse(raw);
  if (!data) return null;
  const next: Param = { ...data, model: modelId };
  const model = GATEWAY_MODELS.find(
    (m) => m.id === (modelId === 'default' ? GATEWAY_DEFAULT_MODEL : modelId)
  );
  const params = parse(next.parameters);
  if (model && params && 'reasoning_effort' in params) {
    const fitted: Param = { ...params };
    if (model.reasoning === 'unsupported') delete fitted.reasoning_effort;
    else if (
      model.reasoning === 'no-none' &&
      fitted.reasoning_effort === 'none'
    )
      fitted.reasoning_effort = 'low';
    next.parameters = fitted;
  }
  return JSON.stringify(next, null, 2);
};

/** The Langfuse span name a step's calls are traced under (`tracing.name`). */
export const stepSpanName = (raw: unknown): string | undefined => {
  const tracing = parse(raw)?.tracing;
  const name =
    tracing && typeof tracing === 'object'
      ? (tracing as Param).name
      : undefined;
  return typeof name === 'string' && name.trim() ? name.trim() : undefined;
};
